import { useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_MERCHANT_CHANNEL_FORM,
  formToPayload,
  rowToForm,
} from './merchantChannelConstants';
import {
  describeCoinPayTypeClassification,
  findCoinPayTypeById,
  formatCoinPayTypeSelectLabel,
  resolveCurrencyFromCoinPayType,
} from './paymentAssetConstants';

export default function MerchantChannelEditModal({
  open,
  row,
  defaultMerchantId,
  merchants,
  coinPayTypes,
  onClose,
  onSubmit,
  submitting,
}) {
  const isEdit = Boolean(row?.id);
  const [form, setForm] = useState(DEFAULT_MERCHANT_CHANNEL_FORM);
  const [error, setError] = useState('');

  const selectedCoinPayType = useMemo(
    () => findCoinPayTypeById(coinPayTypes, form.coinPayTypeId),
    [coinPayTypes, form.coinPayTypeId]
  );

  const classification = useMemo(
    () => describeCoinPayTypeClassification(selectedCoinPayType),
    [selectedCoinPayType]
  );

  useEffect(() => {
    if (open) {
      setForm(rowToForm(row, defaultMerchantId, coinPayTypes));
      setError('');
    }
  }, [open, row, defaultMerchantId, coinPayTypes]);

  if (!open) {
    return null;
  }

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleCoinPayTypeChange(coinPayTypeId) {
    const currencyType = resolveCurrencyFromCoinPayType(coinPayTypes, coinPayTypeId);
    setForm((prev) => ({
      ...prev,
      coinPayTypeId,
      currencyType,
    }));
  }

  function toggleMerchantId(merchantId, checked) {
    const idStr = String(merchantId);
    setForm((prev) => {
      const next = new Set(prev.merchantIds || []);
      if (checked) {
        next.add(idStr);
      } else {
        next.delete(idStr);
      }
      return { ...prev, merchantIds: Array.from(next) };
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!form.name?.trim()) {
      setError('請填寫通道名稱');
      return;
    }
    if (!form.merchantIds?.length) {
      setError('請至少選擇一個綁定商戶');
      return;
    }
    if (!form.coinPayTypeId) {
      setError('請選擇鏈路（幣種）');
      return;
    }
    if (!resolveCurrencyFromCoinPayType(coinPayTypes, form.coinPayTypeId)) {
      setError('所選鏈路缺少幣別，請先在幣種管理補齊');
      return;
    }
    setError('');
    await onSubmit(formToPayload(form, row?.id, coinPayTypes));
  }

  return (
    <div className="admin-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="admin-modal admin-modal--wide"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-modal__header">
          <h3>{isEdit ? '編輯支付通道' : '新增支付通道'}</h3>
          <button type="button" className="admin-modal__close" onClick={onClose}>
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="admin-modal__body">
            <section className="settings-section">
              <h4>基本資料</h4>
              <div className="form-grid">
                <div className="form-field">
                  <label htmlFor="channelName">通道名稱</label>
                  <input
                    id="channelName"
                    type="text"
                    value={form.name}
                    onChange={(e) => updateField('name', e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="channelCoinPayType">鏈路（幣種） *</label>
                  <select
                    id="channelCoinPayType"
                    value={form.coinPayTypeId}
                    required
                    onChange={(e) => handleCoinPayTypeChange(e.target.value)}
                  >
                    {coinPayTypes.length === 0 ? (
                      <option value="">請先在幣種管理新增幣種</option>
                    ) : (
                      coinPayTypes.map((item) => (
                        <option key={item.id} value={item.id}>
                          {formatCoinPayTypeSelectLabel(item)}
                        </option>
                      ))
                    )}
                  </select>
                </div>
                <div className="form-field">
                  <label htmlFor="channelType">通道類型</label>
                  <input
                    id="channelType"
                    type="text"
                    value={form.type}
                    onChange={(e) => updateField('type', e.target.value)}
                    placeholder="可留空"
                  />
                </div>
                <div className="form-field">
                  <label>幣別（自動）</label>
                  <input type="text" value={classification.currency} readOnly />
                </div>
                <div className="form-field">
                  <label>資產大類（自動）</label>
                  <input type="text" value={classification.assetClassLabel} readOnly />
                </div>
                <div className="form-field">
                  <label>支付方式（自動）</label>
                  <input type="text" value={classification.payMethodKindLabel} readOnly />
                </div>
                <div className="form-field">
                  <label htmlFor="channelSort">排序</label>
                  <input
                    id="channelSort"
                    type="number"
                    value={form.sort}
                    onChange={(e) => updateField('sort', e.target.value)}
                  />
                </div>
              </div>
              <div className="form-field">
                <span className="form-label">綁定商戶</span>
                <div className="checkbox-row">
                  {merchants.map((merchant) => (
                    <label key={merchant.id} className="checkbox-inline">
                      <input
                        type="checkbox"
                        checked={form.merchantIds.includes(String(merchant.id))}
                        onChange={(e) => toggleMerchantId(merchant.id, e.target.checked)}
                      />
                      {merchant.name}
                    </label>
                  ))}
                </div>
              </div>
            </section>

            <section className="settings-section">
              <h4>充值設定</h4>
              <div className="form-grid">
                <div className="form-field">
                  <label htmlFor="channelRechargeFee">手續費 (%)</label>
                  <input
                    id="channelRechargeFee"
                    type="number"
                    step="any"
                    value={form.rechargeFee}
                    onChange={(e) => updateField('rechargeFee', e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="channelRechargeMin">最小金額</label>
                  <input
                    id="channelRechargeMin"
                    type="number"
                    step="any"
                    value={form.rechargeMin}
                    onChange={(e) => updateField('rechargeMin', e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="channelRechargeMax">最大金額</label>
                  <input
                    id="channelRechargeMax"
                    type="number"
                    step="any"
                    value={form.rechargeMax}
                    onChange={(e) => updateField('rechargeMax', e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="channelRechargeRebate">返現比例 (%)</label>
                  <input
                    id="channelRechargeRebate"
                    type="number"
                    step="any"
                    value={form.rechargeRebate}
                    onChange={(e) => updateField('rechargeRebate', e.target.value)}
                  />
                </div>
              </div>
            </section>

            <section className="settings-section">
              <h4>提現設定</h4>
              <div className="form-grid">
                <div className="form-field">
                  <label htmlFor="channelWithdrawalFee">手續費 (%)</label>
                  <input
                    id="channelWithdrawalFee"
                    type="number"
                    step="any"
                    value={form.withdrawalFee}
                    onChange={(e) => updateField('withdrawalFee', e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="channelWithdrawalMin">最小金額</label>
                  <input
                    id="channelWithdrawalMin"
                    type="number"
                    step="any"
                    value={form.withdrawalMin}
                    onChange={(e) => updateField('withdrawalMin', e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="channelWithdrawalMax">最大金額</label>
                  <input
                    id="channelWithdrawalMax"
                    type="number"
                    step="any"
                    value={form.withdrawalMax}
                    onChange={(e) => updateField('withdrawalMax', e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="channelWithdrawalRebate">返現比例 (%)</label>
                  <input
                    id="channelWithdrawalRebate"
                    type="number"
                    step="any"
                    value={form.withdrawalRebate}
                    onChange={(e) => updateField('withdrawalRebate', e.target.value)}
                  />
                </div>
              </div>
            </section>

            {error && <div className="alert alert-error">{error}</div>}
          </div>
          <div className="admin-modal__footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
              取消
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? '保存中…' : '保存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
